// frontend/src/components/UI.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const COLORS = {
  AK: '#1F3B68', JT: '#185FA5', LP: '#854F0B',
  MR: '#534AB7', SC: '#A32D2D', SS: '#1F3B68',
};
const BG = {
  AK: '#EAEEF6', JT: '#E6F1FB', LP: '#FAEEDA',
  MR: '#EEEDFE', SC: '#FAECE7', SS: '#EAEEF6',
};

export function Avatar({ initials = '?', size = 36, style = {} }) {
  const bg  = BG[initials]  || '#EAEEF6';
  const col = COLORS[initials] || '#1F3B68';
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: bg, color: col,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.32, fontWeight: 500, flexShrink: 0, ...style
    }}>
      {initials}
    </div>
  );
}

// Clickable avatar + name that routes to a user's profile. Falls back to a
// plain (non-link) block when `id` is missing so it can be used anywhere a
// person is displayed. Hover affordance lives in index.css (.user-link).
export function UserLink({ id, name, initials, subtitle, avatarSize = 30, nameSize = 13, gap = 10, style = {}, onClick }) {
  const body = (
    <>
      <Avatar initials={initials || '?'} size={avatarSize} />
      <div style={{ minWidth: 0 }}>
        <div className="user-link-name" style={{ fontSize: nameSize, fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
        {subtitle && <div style={{ fontSize: Math.max(10, nameSize - 2), color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</div>}
      </div>
    </>
  );
  const layout = { display: 'flex', alignItems: 'center', gap, minWidth: 0, ...style };
  if (!id) return <div style={layout}>{body}</div>;
  return <Link to={`/profile/${id}`} onClick={onClick} className="user-link" style={{ ...layout, textDecoration: 'none' }}>{body}</Link>;
}

export function Badge({ children, color = 'green' }) {
  const map = {
    // 'green' retained as an alias so existing call sites stay valid; it now
    // renders in the brand navy. 'navy'/'yellow' are the brand-forward names.
    green:  { bg: 'var(--navy-50)',    color: 'var(--navy-600)'   },
    navy:   { bg: 'var(--navy-50)',    color: 'var(--navy-600)'   },
    yellow: { bg: 'var(--yellow-50)',  color: 'var(--yellow-700)' },
    amber:  { bg: 'var(--amber-50)',   color: 'var(--amber-700)'  },
    blue:   { bg: 'var(--blue-50)',    color: 'var(--blue-700)'   },
    red:    { bg: 'var(--red-50)',     color: 'var(--red-700)'    },
    gray:   { bg: 'var(--gray-100)',   color: 'var(--gray-700)'   },
  };
  const s = map[color] || map.navy;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: 11, fontWeight: 500, padding: '2px 9px',
      borderRadius: 20, background: s.bg, color: s.color,
    }}>{children}</span>
  );
}

export function Btn({ children, onClick, variant = 'primary', size = 'md', disabled = false, style = {}, type }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    border: 'none', borderRadius: 'var(--radius-md)',
    fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1, transition: 'all 0.15s',
    fontSize: size === 'sm' ? 12 : 13,
    padding: size === 'sm' ? '5px 12px' : '8px 16px',
    ...style
  };
  const variants = {
    primary:   { background: '#1F3B68', color: '#fff' },
    secondary: { background: 'transparent', color: 'var(--text-1)', border: '0.5px solid var(--border-md)' },
    danger:    { background: '#E24B4A', color: '#fff' },
    ghost:     { background: 'transparent', color: 'var(--text-2)', padding: size === 'sm' ? '4px 8px' : '6px 10px' },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{ ...base, ...variants[variant] }}>
      {children}
    </button>
  );
}

export function Card({ children, style = {}, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: 'var(--surface)', border: '0.5px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '16px 18px',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'border-color 0.15s',
      ...style
    }}>
      {children}
    </div>
  );
}

export function MetricCard({ label, value, change, changeType = 'up', onClick }) {
  return (
    <div onClick={onClick} style={{
      background: 'var(--gray-100)', borderRadius: 'var(--radius-md)',
      padding: '14px 16px',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'background 0.15s',
    }}
    onMouseEnter={e => { if (onClick) e.currentTarget.style.background = 'var(--gray-200)'; }}
    onMouseLeave={e => { if (onClick) e.currentTarget.style.background = 'var(--gray-100)'; }}
    >
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--text-3)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 500 }}>{value}</div>
      {change && (
        <div style={{ fontSize: 11, marginTop: 3, color: changeType === 'up' ? 'var(--navy-600)' : 'var(--red-700)' }}>
          {changeType === 'up' ? '▲' : '▼'} {change}
        </div>
      )}
    </div>
  );
}

export function ProgressBar({ value = 0, color = 'var(--navy-500)', height = 5 }) {
  return (
    <div style={{ background: 'var(--gray-100)', borderRadius: 10, height, overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(value, 100)}%`, height: '100%', background: color, borderRadius: 10, transition: 'width 0.4s ease' }} />
    </div>
  );
}

export function Spinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 0' }}>
      <div style={{
        width: 28, height: 28, border: '2.5px solid var(--gray-200)',
        borderTopColor: 'var(--navy-500)', borderRadius: '50%',
        animation: 'spin 0.7s linear infinite'
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export function Modal({ open, onClose, title, children, width = 520 }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
        width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto',
        boxShadow: 'var(--shadow-md)', padding: '24px', margin: '0 12px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontFamily: "'Poppins', sans-serif" }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-3)', cursor: 'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 5, color: 'var(--text-2)' }}>
        {label}{required && <span style={{ color: 'var(--red-500)', marginLeft: 2 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

export function Input({ value, onChange, placeholder, type = 'text', style = {} }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      style={{
        width: '100%', padding: '8px 10px', fontSize: 13,
        border: '0.5px solid var(--border-md)', borderRadius: 'var(--radius-md)',
        background: 'var(--surface)', color: 'var(--text-1)', outline: 'none',
        transition: 'border-color 0.15s', ...style
      }}
      onFocus={e => e.target.style.borderColor = 'var(--navy-500)'}
      onBlur={e => e.target.style.borderColor = 'var(--border-md)'}
    />
  );
}

export function Select({ value, onChange, children, style = {}, disabled = false }) {
  return (
    <select value={value} onChange={onChange} disabled={disabled}
      style={{
        width: '100%', padding: '8px 10px', fontSize: 13,
        border: '0.5px solid var(--border-md)', borderRadius: 'var(--radius-md)',
        background: disabled ? 'var(--gray-50)' : 'var(--surface)',
        color: disabled ? 'var(--text-2)' : 'var(--text-1)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        outline: 'none', ...style
      }}>
      {children}
    </select>
  );
}

export function Textarea({ value, onChange, placeholder, rows = 4, style = {}, readOnly = false }) {
  return (
    <textarea value={value} onChange={onChange} placeholder={readOnly ? '' : placeholder} rows={rows} readOnly={readOnly}
      style={{
        width: '100%', padding: '8px 10px', fontSize: 13,
        border: '0.5px solid var(--border-md)', borderRadius: 'var(--radius-md)',
        background: readOnly ? 'var(--gray-50)' : 'var(--surface)',
        color: readOnly ? 'var(--text-2)' : 'var(--text-1)', outline: 'none',
        resize: 'vertical', cursor: readOnly ? 'default' : 'text', ...style
      }}
      onFocus={e => { if (!readOnly) e.target.style.borderColor = 'var(--navy-500)'; }}
      onBlur={e => e.target.style.borderColor = 'var(--border-md)'}
    />
  );
}

export function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.7px', color: 'var(--text-3)', marginBottom: 10 }}>
      {children}
    </div>
  );
}

// ── Calibration system per Tech Team Recognition & Incentive Program ─────────
// Per-KPI scores: 1=Poor, 2=Needs Improvement, 3=Meets Expectations,
//                 4=Exceeds Expectations, 5=Outstanding
// Final score (Σ weight%×score):
//   4.5–5.0  Outstanding        → annual bonus 100% monthly salary
//   4.0–4.49 Excellent          → 75% monthly salary
//   3.5–3.99 Strong Performance → 50% monthly salary
//   3.0–3.49 Meets Expectations → 25% monthly salary
//   < 3.0    Needs Improvement  → 0%
export const CALIBRATION_MAP = {
  outstanding:       { label: 'Outstanding',        bg: '#FFF7D6',                color: '#7A5E00',           range: '4.5 – 5.0',   bonusPct: 100 },
  excellent:         { label: 'Excellent',           bg: 'var(--navy-50)',        color: 'var(--navy-600)',  range: '4.0 – 4.49',  bonusPct: 75  },
  strong:            { label: 'Strong Performance',  bg: 'var(--blue-50)',         color: 'var(--blue-700)',   range: '3.5 – 3.99',  bonusPct: 50  },
  meets:             { label: 'Meets Expectations',  bg: 'var(--amber-50)',        color: 'var(--amber-700)',  range: '3.0 – 3.49',  bonusPct: 25  },
  needs_improvement: { label: 'Needs Improvement',   bg: 'var(--red-50)',          color: 'var(--red-700)',    range: '< 3.0',       bonusPct: 0   },
  // legacy aliases kept for backward compat
  exceptional:       { label: 'Outstanding',         bg: '#FFF7D6',                color: '#7A5E00',           range: '4.5 – 5.0',   bonusPct: 100 },
  good:              { label: 'Meets Expectations',  bg: 'var(--amber-50)',        color: 'var(--amber-700)',  range: '3.0 – 3.49',  bonusPct: 25  },
  below:             { label: 'Needs Improvement',   bg: 'var(--red-50)',          color: 'var(--red-700)',    range: '< 3.0',       bonusPct: 0   },
  exceeds:           { label: 'Excellent',           bg: 'var(--navy-50)',        color: 'var(--navy-600)',  range: '4.0 – 4.49',  bonusPct: 75  },
};

export function scoreToCalibration(score) {
  const s = parseFloat(score);
  if (isNaN(s)) return null;
  if (s >= 4.5) return 'outstanding';
  if (s >= 4.0) return 'excellent';
  if (s >= 3.5) return 'strong';
  if (s >= 3.0) return 'meets';
  return 'needs_improvement';
}

export function scoreToLabel(score) {
  const cal = scoreToCalibration(score);
  return CALIBRATION_MAP[cal]?.label || '—';
}

export const QUARTERLY_REWARDS = {
  Developer: 10000,
  QA:        5000,
  TechLead:  10000,
  Architect: 10000,
  PM:        0,
};

export const ROLE_TYPE_COLORS = {
  Developer: { bg: '#E6F1FB', color: '#185FA5' },
  QA:        { bg: '#EAEEF6', color: '#1F3B68' },
  TechLead:  { bg: '#FFF7D6', color: '#7A5E00' },
  Architect: { bg: '#EEEDFE', color: '#534AB7' },
  PM:        { bg: '#FAEEDA', color: '#854F0B' },
};

export const KPI_SCORE_LABELS = {
  1: 'Poor',
  2: 'Needs Improvement',
  3: 'Meets Expectations',
  4: 'Exceeds Expectations',
  5: 'Outstanding',
};

export function RatingPill({ value, label }) {
  const s = CALIBRATION_MAP[value] || { bg: 'var(--color-background-secondary)', color: 'var(--color-text-secondary)', label: value };
  const displayLabel = label || s.label || value;
  return (
    <span style={{ display: 'inline-flex', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: s.bg, color: s.color }}>
      {displayLabel}
    </span>
  );
}

export function Stars({ value = 0, max = 5 }) {
  return (
    <span style={{ color: 'var(--amber-500)', fontSize: 14 }}>
      {Array.from({ length: max }, (_, i) => i < Math.round(value) ? '★' : '☆').join('')}
    </span>
  );
}
